import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMedia } from 'react-use';
import { Button, cn } from '@c2fo/liquidity';
import colors from '@c2fo/liquidity/colors';
import { ArrowDownIcon, ArrowUpIcon } from '@c2fo/liquidity/icons';
import Skeleton from './Skeleton';

/** TABLE */
export const Table = forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => <table ref={ref} className={cn('w-full', className)} {...props} />
);
Table.displayName = 'Table';

/** TABLE HEADER */
export const TableHeader = forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <thead ref={ref} className={cn('border-b border-stroke bg-canvas [&_tr]:border-b-0', className)} {...props} />
  )
);
TableHeader.displayName = 'TableHeader';

/** TABLE BODY */
interface TableSectionElement extends React.HTMLAttributes<HTMLTableSectionElement> {
  /**
   * Whether the table is loading or not.
   */
  loading?: boolean;
}

export const TableBody = forwardRef<HTMLTableSectionElement, TableSectionElement>(
  ({ className, loading = false, ...props }, ref) => (
    <tbody
      ref={ref}
      className={cn('[&_tr:last-child]:border-0', className, {
        'blur-sm': loading,
      })}
      {...props}
    />
  )
);
TableBody.displayName = 'TableBody';

/** TABLE ROW */
export const TableRow = forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => <tr ref={ref} className={cn('border-b', className)} {...props} />
);
TableRow.displayName = 'TableRow';

/** TABLE HEAD */
export const TableHead = forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement> & { isSorted?: boolean }
>(({ className, isSorted = false, ...props }, ref) => (
  <th
    ref={ref}
    scope="col"
    className={cn(
      'whitespace-nowrap bg-canvas p-4 text-left text-sm font-bold text-deepBlue-900 first-of-type:pl-4 last-of-type:pr-4',
      { 'cursor-pointer transition-all duration-200 hover:bg-gray-100/50': props.onClick },
      { 'bg-gray-100 hover:bg-gray-100': isSorted },
      className
    )}
    {...(props.onClick && { role: 'button' })}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

/** TABLE CELL */
export const TableCell = forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <td
      ref={ref}
      className={cn('whitespace-nowrap bg-white p-4 first-of-type:pl-4 last-of-type:pr-4', className)}
      {...props}
    />
  )
);
TableCell.displayName = 'TableCell';

/** TABLE MESSAGE */
export const TableMessage = ({ colSpan, message }: { colSpan: number; message: string }) => (
  <tr>
    <TableCell className="text-center text-gray-500" colSpan={colSpan}>
      {message}
    </TableCell>
  </tr>
);
TableMessage.displayName = 'TableMessage';

/** TABLE DISPLAY */
export const TableDisplay = ({ colSpan, children }: { colSpan: number; children: React.ReactNode }) => (
  <tr>
    <TableCell colSpan={colSpan}>{children}</TableCell>
  </tr>
);
TableDisplay.displayName = 'TableDisplay';

/** TABLE DISPLAY SKELETON ROWS */
export const TableDisplaySkeletonRows = ({
  colSpan,
  rowCount = 25,
  height = 'h-10',
}: {
  colSpan: number;
  rowCount?: number;
  /** Tailwind h- class for each row's skeleton. */
  height?: string;
}) => {
  return Array.from({ length: rowCount }).map((_, index) => (
    <tr key={index}>
      <TableCell colSpan={colSpan}>
        <Skeleton key={index} height={height} />
      </TableCell>
    </tr>
  ));
};

TableDisplaySkeletonRows.displayName = 'TableDisplaySkeletonRows';

// TABLE PAGINATION
export const TablePagination = ({
  className,
  onNextChange,
  onPrevChange,
  onPageSizeChange,
  pageSize,
  disablePrev,
  disableNext,
  currentPage,
}: {
  className?: string;
  disableNext?: boolean;
  disablePrev?: boolean;
  currentPage?: number;
  onNextChange: () => void;
  onPrevChange: () => void;
  onPageSizeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  pageSize: number;
}) => {
  const { t } = useTranslation();
  const isMobile = useMedia('(max-width: 768px)');

  return (
    <div
      className={cn(
        'flex flex-col-reverse items-center justify-between gap-y-4 border-t px-6 py-4 sm:flex-row sm:gap-y-0 sm:px-8 sm:py-6',
        className
      )}
    >
      <div className="flex items-center gap-2">
        <div>{t('taker.recordsPerPage')}</div>
        <select className="rounded-md border p-1.5" onChange={(e) => onPageSizeChange(e)} value={pageSize}>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>
      {currentPage && (
        <div className="text-sm text-text-secondary">{t('core.pageNumber', { formattedPageNumber: currentPage })}</div>
      )}
      <div className="flex w-full items-center justify-between gap-2 sm:w-auto">
        <div>
          <Button
            variant="secondary"
            size={isMobile ? 'md' : 'sm'}
            onClick={onPrevChange}
            disabled={disablePrev ?? false}
          >
            {t('core.previousPage')}
          </Button>
        </div>
        <div>
          <Button
            variant="secondary"
            size={isMobile ? 'md' : 'sm'}
            onClick={onNextChange}
            disabled={disableNext ?? false}
          >
            {t('core.nextPage')}
          </Button>
        </div>
      </div>
    </div>
  );
};

TablePagination.displayName = 'TablePagination';

/** TABLE SORT ARROW */
export type TableSortDirection = 'asc' | 'desc';

export function TableSortArrow<T>({
  accessorKey,
  children,
  sort,
  textCenter,
  textRight,
}: {
  accessorKey: T;
  children: React.ReactNode;
  sort: { key: T; direction: TableSortDirection } | null;
  textCenter?: boolean;
  textRight?: boolean;
}) {
  const arrowIconProps = {
    fill: sort?.key === accessorKey ? colors.secondary[500] : 'transparent',
  };
  const ArrowIcon = (
    <div>
      {sort?.direction === 'desc' ? <ArrowDownIcon {...arrowIconProps} /> : <ArrowUpIcon {...arrowIconProps} />}
    </div>
  );

  return (
    <div
      className={cn('flex items-center justify-start space-x-1', {
        'justify-end': textRight,
        'justify-center': textCenter,
      })}
    >
      {textRight && ArrowIcon}
      <div>{children}</div>
      {!textRight && ArrowIcon}
    </div>
  );
}
